import React, { useState, useEffect } from "react";

const initialState = {
  date: "",
  name: "",
  phone_number: "",
  received: "",
  sent: "",
  balance: "",
  remarks: "",
};

const INRInvestorForm = ({ onAdd }) => {
  const [form, setForm] = useState(initialState);

  // Function to calculate balance
  const calculateBalance = (received, sent) => {
    const receivedNum = parseFloat(received) || 0;
    const sentNum = parseFloat(sent) || 0;
    const balance = receivedNum - sentNum;
    // Return as number to preserve precision, will format for display only
    return balance;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedForm = { ...form, [name]: value };

    // Auto-calculate balance when received or sent amounts change
    if (name === "received" || name === "sent") {
      const received = name === "received" ? value : form.received;
      const sent = name === "sent" ? value : form.sent;

      // Only calculate if both values exist or if one is being cleared
      if (received !== "" || sent !== "") {
        updatedForm.balance = calculateBalance(received, sent);
      }
    }

    setForm(updatedForm);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (onAdd) onAdd(form);
    setForm(initialState);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white p-6 rounded-lg shadow-md space-y-6"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Date *
          </label>
          <input
            type="date"
            name="date"
            value={form.date}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Name *
          </label>
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            required
            placeholder="Enter full name"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Phone Number *
          </label>
          <input
            type="tel"
            name="phone_number"
            value={form.phone_number}
            onChange={handleChange}
            required
            placeholder="Enter phone number"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Received Amount
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3">
              <span className="text-gray-500 font-medium">₹</span>
            </div>
            <input
              type="number"
              name="received"
              value={form.received}
              onChange={handleChange}
              step="0.01"
              placeholder="0.00"
              className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Sent Amount
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3">
              <span className="text-gray-500 font-medium">₹</span>
            </div>
            <input
              type="number"
              name="sent"
              value={form.sent}
              onChange={handleChange}
              step="0.01"
              placeholder="0.00"
              className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Balance
            <span className="text-xs text-gray-500 ml-2">
              (Auto-calculated)
            </span>
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3">
              <span className="text-gray-500 font-medium">₹</span>
            </div>
            <input
              type="text"
              name="balance"
              value={
                form.balance !== "" &&
                form.balance !== null &&
                form.balance !== undefined
                  ? parseFloat(form.balance).toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  : ""
              }
              readOnly
              placeholder="0.00"
              className={`w-full pl-8 pr-10 py-3 border border-gray-300 rounded-lg shadow-sm bg-gray-50 cursor-not-allowed transition-all duration-200 ${
                form.balance !== "" &&
                form.balance !== null &&
                form.balance !== undefined
                  ? parseFloat(form.balance) >= 0
                    ? "text-green-700 font-semibold"
                    : "text-red-700 font-semibold"
                  : "text-gray-700"
              }`}
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              <svg
                className="w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 14h.01M12 11h.01M9 11h.01M15 11h.01M10 6V4a2 2 0 012-2h0c1.1 0 2 .9 2 2v2"
                />
              </svg>
            </div>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Balance = Received Amount - Sent Amount
          </p>
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Remarks
        </label>
        <textarea
          name="remarks"
          value={form.remarks}
          onChange={handleChange}
          rows={3}
          placeholder="Enter any additional remarks..."
          className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 resize-none"
        />
      </div>

      <div className="flex justify-end pt-4">
        <button
          type="submit"
          className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 font-semibold"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
            />
          </svg>
          Add INR Investor
        </button>
      </div>
    </form>
  );
};

export default INRInvestorForm;
